
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { usePetCategoryCounts } from '../../hooks/usePublicPets';
import { Skeleton } from '../ui/skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Briefcase, ArrowRight } from 'lucide-react';
import { SERVICE_CATEGORIES } from '@/config/serviceCategories';

interface CategoriesModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const CategoriesModal = ({ isOpen, onClose }: CategoriesModalProps) => {
  const { data: categoryCounts, isLoading } = usePetCategoryCounts();
  const { t } = useTranslation();

  const categoryConfig = [
    { id: 'all', name: t('categories.allPets', 'All Pets'), emoji: '🌟' },
    { id: 'dog', name: t('categories.dogs', 'Dogs'), emoji: '🐕' },
    { id: 'cat', name: t('categories.cats', 'Cats'), emoji: '🐱' },
    { id: 'bird', name: t('categories.birds', 'Birds'), emoji: '🐦' },
    { id: 'fish', name: t('categories.fish', 'Fish'), emoji: '🐠' },
    { id: 'reptile', name: t('categories.reptiles', 'Reptiles'), emoji: '🦎' },
    { id: 'small_pet', name: t('categories.smallPets', 'Small Pets'), emoji: '🐹' },
    { id: 'horse', name: t('categories.horses', 'Horses'), emoji: '🐴' },
    { id: 'cattle', name: t('categories.cattle', 'Cattle'), emoji: '🐄' },
    { id: 'other', name: t('categories.other', 'Other'), emoji: '🐾' },
  ];


  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-center">{t('feed.browseCategories')}</DialogTitle>
        </DialogHeader>
        
        <div className="py-4 space-y-3">
          {/* Pet Categories Section */}
          <div>
            <h3 className="text-sm font-semibold text-gray-700 mb-1 px-1">{t('categories.petCategories')}</h3>
            {isLoading ? (
              <div className="grid grid-cols-2 gap-3">
                {Array.from({ length: 8 }).map((_, i) => (
                  <Skeleton key={i} className="h-20 rounded-lg" />
                ))}
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-2">
                {categoryConfig.map((category) => {
                  const count = categoryCounts?.[category.id] || 0;
                  
                  return (
                    <Link
                      key={category.id}
                      to={`/pets/category/${category.id}`}
                      onClick={onClose}
                      className="group bg-gray-50 rounded-lg p-3 text-center hover:shadow-md hover:bg-pet-purple/5 transition-all border border-gray-200"
                    >
                      <div className="text-2xl mb-2">{category.emoji}</div>
                      <div className="text-sm font-medium text-gray-700 group-hover:text-pet-purple transition-colors mb-1">
                        {category.name}
                      </div>
                      {count > 0 && (
                        <div className="text-xs text-gray-500">
                          {count} pets
                        </div>
                      )}
                    </Link>
                  );
                })}
              </div>
            )}
            <div className="text-xs text-center text-muted-foreground py-2 animate-pulse">
              ↓ {t('services.scrollHint', { defaultValue: 'Scroll for Pet Services' })}
            </div>
          </div>

          {/* Services Section */}
          <div>
            <h3 className="text-sm font-semibold text-gray-700 mb-3 px-1">{t('services.petServices')}</h3>
            
            {/* Main "Browse All Services" button */}
            <Link
              to="/services"
              onClick={onClose}
              className="group flex items-center justify-between bg-gradient-to-r from-pet-purple to-pet-blue rounded-lg p-4 hover:shadow-lg transition-all text-white"
            >
              <div className="flex items-center gap-3">
                <Briefcase className="h-6 w-6" />
                <div>
                  <div className="font-semibold text-base">
                    {t('services.title')}
                  </div>
                  <div className="text-sm text-white/80">
                    {t('services.subtitle')}
                  </div>
                </div>
              </div>
              <ArrowRight className="h-5 w-5" />
            </Link>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CategoriesModal;
